import { useCallback, useEffect, useState } from 'react';
import { Check, CircleCheck, DatabaseZap, Loader2, RefreshCw, ShieldAlert, TriangleAlert } from 'lucide-react';
import * as api from '../worlds.api';
import { toast } from '../worlds.store';
import { fmtDate, fmtInt } from '../constants';
import type { ImportCounts, ImportPreview, ImportReport, ImportRosterRisk, ImportVerify } from '../worlds.types';
import { EmptyState, InfoBox, StatusBadge } from './ui';
import styles from '../../LootboxPage.module.scss';
import ws from '../WorldsPage.module.scss';

// ============================================================
// Import des données NG Java (ressource embarquée dans le satellite)
// ============================================================
// Idempotent : « create » sur une base vide importe ; sur une base déjà importée à l'identique, le
// serveur répond « unchanged » ; sinon 409. « replace » vide tout avant d'importer et exige de
// taper « remplacer » (la confirmation voyage dans le corps : la passerelle ne relaie pas d'en-tête).
// Si des pays fondés en jeu quitteraient le roster, le satellite suspend le push automatique : il
// faut alors « Synchroniser en jeu » à la main (onglet Pays), après vérification.
// ============================================================

const COUNT_LABELS: [keyof ImportCounts, string][] = [
  ['countries', 'Pays'],
  ['enabledCountries', 'Pays actifs'],
  ['chunks', 'Chunks peints'],
  ['blocks', 'Blocs de grille'],
  ['countriesWithChunks', 'Pays avec des chunks'],
  ['geoRegions', 'Géo-régions'],
  ['levels', 'Niveaux'],
  ['biomes', 'Biomes'],
  ['markers', 'Marqueurs'],
  ['gridSha256', 'Empreinte de la grille'],
  ['rosterSha256', 'Empreinte du roster'],
];

const CONFIRM_WORD = 'remplacer';

function show(v: string | number | undefined): string {
  if (v === undefined) return '';
  return typeof v === 'number' ? fmtInt(v) : `${v.slice(0, 12)}…`;
}

function CountsTable({ expected, actual }: { expected: ImportCounts; actual?: ImportCounts }) {
  return (
    <table className={ws.table}>
      <thead>
        <tr><th>Mesure</th><th className={ws.num}>Attendu</th>{actual && <th className={ws.num}>Obtenu</th>}</tr>
      </thead>
      <tbody>
        {COUNT_LABELS.map(([k, label]) => {
          const bad = !!actual && actual[k] !== expected[k];
          const hash = typeof expected[k] === 'string';
          return (
            <tr key={k} className={bad ? ws.rowBad : undefined}>
              <td>{label}</td>
              <td className={hash ? ws.mono : ws.num} title={String(expected[k])}>{show(expected[k])}</td>
              {actual && (
                <td className={hash ? ws.mono : ws.num} title={String(actual[k])}>
                  <span className={ws.inlineIcon}>
                    {show(actual[k])}
                    {bad
                      ? <TriangleAlert size={12} className={ws.textBad} aria-label="Écart" />
                      : <Check size={12} className={ws.textOk} aria-label="Conforme" />}
                  </span>
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function RosterRiskNote({ risk }: { risk: ImportRosterRisk | null }) {
  if (!risk) return null;
  if (!risk.available) {
    return (
      <InfoBox tone="warn" icon={ShieldAlert} title="Possession en jeu inconnue">
        {risk.message ?? 'NGCountryAPI indisponible'} : après l'import, la synchro du roster attendra un
        « Synchroniser en jeu » manuel.
      </InfoBox>
    );
  }
  if (risk.takenLeaving.length === 0) return null;
  return (
    <InfoBox tone="warn" title={`${fmtInt(risk.takenLeaving.length)} pays fondé(s) en jeu absent(s) de la ressource`}>
      {risk.takenLeaving.slice(0, 20).join(', ')}{risk.takenLeaving.length > 20 ? '…' : ''}. Après l'import, la
      synchro automatique sera suspendue : recrée-les ici, ou synchronise à la main pour les délister (la possession
      est conservée par NGCountryAPI).
    </InfoBox>
  );
}

function ReportView({ report }: { report: ImportReport | ImportVerify }) {
  const status = 'status' in report ? report.status : null;
  const rosterWarning = 'rosterWarning' in report ? report.rosterWarning : null;
  return (
    <div className={ws.report}>
      <div className={ws.reportHead}>
        {report.ok
          ? <StatusBadge tone="ok" icon={CircleCheck}>Conforme</StatusBadge>
          : <StatusBadge tone="bad" icon={TriangleAlert}>{fmtInt(report.mismatches.length)} écart(s)</StatusBadge>}
        <span>
          {status === 'imported' ? 'Données importées. ' : status === 'replaced' ? 'Données remplacées. ' : status === 'unchanged' ? 'Déjà importées à l\'identique. ' : ''}
          {report.ok ? 'Vérification conforme à la ressource.' : 'La base diffère de la ressource sur les mesures signalées.'}
          {'durationMs' in report ? ` (${fmtInt(report.durationMs)} ms)` : ''}
        </span>
      </div>
      {rosterWarning && <InfoBox tone="warn" title="Synchro automatique du roster suspendue">{rosterWarning}</InfoBox>}
      {report.mismatches.length > 0 && (
        <ul className={ws.mismatches}>{report.mismatches.map((m) => <li key={m}>{m}</li>)}</ul>
      )}
      <CountsTable expected={report.expected} actual={report.actual} />
      <p className={styles.muted}>Ressource sha256 : <code className={ws.codeChip}>{report.resourceSha256}</code> · niveau {report.level}</p>
    </div>
  );
}

export function ImportPanel({ inModal = false, readOnly, onImported }: {
  /** Données déjà présentes : aperçu + vérification + remplacement. */
  inModal?: boolean;
  readOnly: boolean;
  onImported: () => void;
}) {
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [verify, setVerify] = useState<ImportVerify | null>(null);
  const [report, setReport] = useState<ImportReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState('');
  // Hors modale, « create » répond 409 si des données restent (ex. biomes seuls) : on propose alors
  // le remplacement ici, sinon la page n'offrirait aucun moyen d'en sortir.
  const [offerReplace, setOfferReplace] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const [p, v] = await Promise.all([api.getImportPreview(), inModal ? api.verifyImport() : Promise.resolve(null)]);
    setLoading(false);
    if (p.data) setPreview(p.data); else setError(p.error ?? 'Aperçu indisponible');
    if (v?.data) setVerify(v.data);
  }, [inModal]);

  useEffect(() => { void load(); }, [load]);

  const run = async (mode: 'create' | 'replace') => {
    setBusy(true);
    setError(null);
    const res = await api.runImport(mode === 'replace' ? { mode, confirm: CONFIRM_WORD } : { mode });
    setBusy(false);
    if (!res.data) {
      setError(res.error ?? 'Import en échec');
      if (mode === 'create' && res.status === 409) setOfferReplace(true);
      return;
    }
    setReport(res.data);
    setConfirm('');
    if (res.data.ok) toast.success(res.data.status === 'unchanged' ? 'Données NG Java déjà importées' : 'Données NG Java importées');
    else toast.warning('Import terminé avec des écarts : voir le rapport');
    onImported();
  };

  if (loading) return <EmptyState icon={Loader2} spin compact title="Lecture de la ressource d'import…" />;

  return (
    <div className={ws.importPanel}>
      {!inModal ? (
        <>
          <EmptyState icon={DatabaseZap} title="Aucune donnée de monde"
            text="La base du module Mondes est vide : importe la carte de NG Java pour commencer." />
          <InfoBox icon={DatabaseZap} title="Ce que l'import reprend">
            Pays actifs, drapeaux, géo-régions, niveau overworld, biomes et marqueurs. Les pays inactifs ne sont pas
            repris ; leurs chunks restent sans pays. L'import est vérifié mesure par mesure contre la ressource.
          </InfoBox>
        </>
      ) : (
        <InfoBox title="Vérifier, puis importer ou remplacer">
          Le tableau compare la base à la ressource embarquée dans le satellite. Le remplacement vide toutes les données
          du module avant de réimporter la ressource.
        </InfoBox>
      )}

      {error && <InfoBox tone="bad" title="Import en échec">{error}</InfoBox>}

      {preview && !preview.available && (
        <InfoBox tone="warn" title="Ressource absente">Ce satellite n'embarque pas la ressource d'import NG Java.</InfoBox>
      )}

      {preview?.available && preview.expected && !report && (
        <>
          <p className={styles.muted}>
            Export du {fmtDate(preview.exportedAt)} · ressource <code className={ws.codeChip}>{preview.resourceSha256?.slice(0, 16)}…</code>
            {preview.lastImport && <> · dernier import le {fmtDate(preview.lastImport.importedAt)} par {preview.lastImport.importedBy} ({preview.lastImport.mode})</>}
          </p>
          {preview.alreadyImported && (
            <div className={ws.reportHead}>
              <StatusBadge tone="ok" icon={CircleCheck}>Déjà importées</StatusBadge>
              <span className={styles.muted}>Données déjà importées depuis cette ressource.</span>
            </div>
          )}
          {verify ? <ReportView report={verify} /> : <CountsTable expected={preview.expected} />}
          <RosterRiskNote risk={preview.rosterRisk} />
          {inModal && (
            <p className={styles.muted}>
              En base : {fmtInt(preview.existing.countries)} pays, {fmtInt(preview.existing.chunks)} chunks,
              {' '}{fmtInt(preview.existing.geoRegions)} géo-régions, {fmtInt(preview.existing.levels)} niveau(x),
              {' '}{fmtInt(preview.existing.biomes)} biomes, {fmtInt(preview.existing.markers)} marqueurs.
            </p>
          )}
        </>
      )}

      {report && <ReportView report={report} />}

      {!readOnly && preview?.available && (
        <div className={ws.importActions}>
          {!inModal && !report && (
            <button type="button" className={styles.primaryBtn} onClick={() => void run('create')} disabled={busy}>
              {busy ? <Loader2 size={14} className={ws.spinning} /> : <DatabaseZap size={14} />} Importer les données NG Java
            </button>
          )}
          {(inModal || offerReplace) && (
            <>
              {inModal && preview.empty && (
                <button type="button" className={styles.primaryBtn} onClick={() => void run('create')} disabled={busy}>
                  {busy ? <Loader2 size={14} className={ws.spinning} /> : <DatabaseZap size={14} />} Importer les données NG Java
                </button>
              )}
              <div className={ws.replaceBox}>
                <span className={ws.replaceTitle}><TriangleAlert size={14} aria-hidden /> Remplacer toutes les données</span>
                <p className={styles.muted}>
                  Vide pays, grille, géo-régions, niveaux, biomes et marqueurs, puis réimporte la ressource.
                  Les modifications faites depuis le panel sont perdues. Tape « {CONFIRM_WORD} » pour confirmer.
                </p>
                <div className={ws.inlineActions}>
                  <input className={`${styles.input} ${ws.mono}`} style={{ maxWidth: 180 }} value={confirm} placeholder={CONFIRM_WORD}
                    aria-label={`Taper ${CONFIRM_WORD} pour confirmer`} onChange={(e) => setConfirm(e.target.value)} />
                  <button type="button" className={`${styles.secondaryBtn} ${ws.dangerBtn}`}
                    disabled={busy || confirm !== CONFIRM_WORD} onClick={() => void run('replace')}>
                    {busy ? <Loader2 size={14} className={ws.spinning} /> : <RefreshCw size={14} />} Remplacer toutes les données
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
